import {
  createContext, useCallback, useMemo, useState,
} from 'react';
import Template from '../models/Template/Template';
import templateAPI from '../services/template';
import { PaginationMetaLink } from '../models/Paginator';

interface TemplatesContextProps {
  templateList?: Template[],
  getAllTemplates: (page?: number, perPage?: number, searchText?: string) => Promise<{
    currentPage: number,
    total: number,
    links: PaginationMetaLink[]
  }>,
  getTemplatesFromCategory: (id: string, options:{ page?: number, type?: 'system-block' | 'my-block', searchText?: string }) => Promise<{
    currentPage: number,
    data: Template[],
    total: number,
    links: PaginationMetaLink[]
  }>
  submitTemplate: (template: Template, cb?: () => void) => void,
  destroyTemplate: (id: string, cb?: () => void) => void
}

export const TemplatesContext = createContext<TemplatesContextProps>({} as TemplatesContextProps);
TemplatesContext.displayName = 'TemplatesContext';

interface Props {
  children: JSX.Element
}

export const TemplatesProvider = ({ children }: Props) => {
  const [templateList, setTemplateList] = useState<Template[]>([]);

  const getAllTemplates = useCallback(async (page?:number, perPage?: number, searchText?: string): Promise<{
    currentPage: number,
    links: PaginationMetaLink[],
    total: number,
  }> => {
    const result = await templateAPI.getAllTemplates(page, perPage, 'my-block', searchText);
    setTemplateList(result.data);
    return {
      links: result.meta.links,
      currentPage: result.meta.current_page,
      total: result.meta.total,
    };
  }, []);

  const getTemplatesFromCategory = useCallback(async (categoryId:string, options:{ page?: number, type?: 'system-block' | 'my-block', searchText?: string }): Promise<{
    currentPage: number,
    links: PaginationMetaLink[],
    total: number,
    data: Template[],
  }> => {
    const { page, type, searchText } = options;
    const templatesResult = (await templateAPI.getTemplates(categoryId, page, type, searchText));
    return {
      data: templatesResult.data,
      currentPage: templatesResult.currentPage,
      links: templatesResult.links,
      total: templatesResult.total,
    };
  }, []);

  const submitTemplate = useCallback((data:Template, cb?: () => void) => {
    if (data && data.id) {
      templateAPI.put(data).then(() => {
        console.log('update template successfully');
        // fetch data again
        if (cb != null) {
          cb();
        }
      }).catch(() => {
        console.log('update template int failure');
      });
    } else {
      templateAPI.post(data).then(() => {
        if (cb != null) {
          cb();
        }
      }).catch((err) => {
        console.log('update template in failure');
        console.error(err);
      });
    }
  }, []);

  const destroyTemplate = useCallback((id: string, cb?: () => void) => {
    templateAPI.destroy(id).then(() => {
      // fetch data again
      // 既存のブロックリストから削除します。
      setTemplateList((list) => list.filter((l) => l.id !== id));
      cb();
    }).catch((err) => {
      console.error(err);
    });
  }, []);

  const value = useMemo(() => ({
    templateList,
    getAllTemplates,
    getTemplatesFromCategory,
    destroyTemplate,
    submitTemplate,
  }), [getAllTemplates, getTemplatesFromCategory, destroyTemplate, templateList, submitTemplate]);

  return (
    <TemplatesContext.Provider value={value}>
      {children}
    </TemplatesContext.Provider>
  );
};
